import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#000000' }}>Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="mb-6" style={{ color: '#333333' }}>
              <strong>Effective Date:</strong> October 15, 2025<br />
              <strong>Last Updated:</strong> October 15, 2025
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>1. Introduction</h2>
            <p className="mb-4" style={{ color: '#000000' }}>
              Welcome to Quixocial ("we," "our," or "us"). This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our social media management platform and related services (the "Service").
            </p>
            <p className="mb-4" style={{ color: '#000000' }}>
              By using our Service, you agree to this Privacy Policy. If you do not agree, please discontinue use of the Service.
            </p>
            <p className="mb-6" style={{ color: '#000000' }}>
              This Policy applies to all users globally, including those located in the EU/EEA, the United Kingdom, the United States, and other regions with applicable privacy regulations.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>2.1 Personal Information</h3>
            <p className="mb-4" style={{ color: '#000000' }}>We collect the following personal information when you register and use our Service:</p>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Account Information:</h4>
              <ul className="list-disc list-inside space-y-1" style={{ color: '#000000' }}>
                <li>Full name (first and last name)</li>
                <li>Email address</li>
                <li>Username</li>
                <li>Password (encrypted)</li>

                <li>Profile picture/avatar</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Business Information:</h4>
              <ul className="list-disc list-inside space-y-1" style={{ color: '#000000' }}>
                <li>Company/client name</li>
                <li>Business description</li>
                <li>Industry type</li>
                <li>Business logo and brand assets</li>
              </ul>
            </div>

            <p className="mb-6" style={{ color: '#000000' }}>
              We may also collect limited technical data automatically, such as IP address, browser type, device identifiers, and usage logs, to improve platform performance and security.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>2.2 Social Media Account Information</h3>
            <p className="mb-4" style={{ color: '#000000' }}>When you connect your social media accounts, we collect:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Facebook/Meta Integration:</h4>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Page access tokens</li>
                  <li>Page information</li>
                  <li>Publishing permissions</li>
                  <li>Analytics data</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Instagram Integration:</h4>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Business account info</li>
                  <li>Media permissions</li>
                  <li>Account insights</li>
                  <li>Analytics data</li>
                </ul>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>X (Twitter) Integration:</h4>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Account access tokens</li>
                  <li>Profile information</li>
                  <li>Tweet permissions</li>
                  <li>Engagement metrics</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>YouTube Integration:</h4>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Channel information</li>
                  <li>Upload permissions</li>
                  <li>Video metadata</li>
                  <li>Analytics data</li>
                </ul>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Pinterest Integration:</h4>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Board access</li>
                  <li>Pin permissions</li>
                  <li>Account information</li>
                  <li>Engagement data</li>
                </ul>
              </div>

            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>2.3 AI-Powered Content Data</h3>
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>To provide AI-generated content and automation features, we may process:</p>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Text, captions, and images you upload</li>
                <li>Brand voice preferences</li>
                <li>Logos, product images, and creative assets</li>
                <li>Industry and content-type information</li>
              </ul>
              <p className="mt-4" style={{ color: '#000000' }}>
                This information may be securely transmitted to Google Gemini AI and Meshy AI for processing. Data is only used to generate content for your account and is not used to train external AI models.
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>2.4 Payment Information</h3>
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                Payments are processed by Stripe, a third-party payment provider. Quixocial does not store or process your credit card details directly. Stripe handles all payment data in accordance with its Privacy Policy.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>3. How We Use Your Information</h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>Service Provision</h3>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Create and manage your account</li>
                <li>Provide social media management tools</li>
                <li>Generate and schedule content</li>
                <li>Publish posts to connected platforms</li>
                <li>Provide analytics and reporting</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>AI-Powered Features</h3>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Generate content using AI</li>
                <li>Create personalized content recommendations</li>
                <li>Optimize posting schedules</li>
                <li>Provide hashtag suggestions</li>
                <li>Provide caption suggestions</li>
                <li>Enhance product images for content</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>Third-Party Service Providers</h3>
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>We work with trusted third parties who process data on our behalf:</p>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Amazon Web Services (AWS) – cloud hosting and storage</li>
                <li>Google Gemini AI – AI content generation</li>
                <li>Stripe – payment processing</li>
                <li>Analytics providers – usage analytics and cookies</li>
              </ul>
              <p className="mt-4" style={{ color: '#000000' }}>
                All subprocessors are bound by confidentiality and data protection agreements.
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>Communications</h3>
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                We may also use your email address to send service updates, support communications, and (with your consent) marketing or promotional messages. You can opt out at any time.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>4. User Content and Responsibilities</h2>
            
            <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>User Content Responsibility</h3>
              <p className="mb-4" style={{ color: '#000000' }}>
                Users are solely responsible for any content they create, schedule, or publish via Quixocial, including text, images, videos, and AI-generated content. Quixocial does not post content on behalf of users without their explicit action or authorization. Users must ensure that their content complies with the terms of service and content policies of connected social media platforms.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>AI-Generated Content</h3>
              <p className="mb-4" style={{ color: '#000000' }}>
                Quixocial provides AI-powered tools to help users generate content, including text, captions, hashtags, and images. Users must review, approve, and schedule AI-generated content before it is published to any connected social media accounts. Quixocial does not post AI-generated content automatically without user consent.
              </p>
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>Social Media Integration</h3>
              <p className="mb-4" style={{ color: '#000000' }}>
                Users must connect their social media accounts to Quixocial using OAuth authorization. Quixocial only accesses, schedules, and publishes content to these accounts after explicit user authorization. Access tokens and permissions are securely stored and used solely for providing the requested services.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>5. Data Security</h2>
            <div className="bg-gray-900 text-white p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3">Security Measures</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Encryption:</h4>
                  <p className="text-gray-300 text-sm">All data encrypted in transit and at rest</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Authentication:</h4>
                  <p className="text-gray-300 text-sm">Secure login with password hashing</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Access Controls:</h4>
                  <p className="text-gray-300 text-sm">Role-based access to user data</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Infrastructure:</h4>
                  <p className="text-gray-300 text-sm">AWS cloud with enterprise security</p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                In the unlikely event of a data breach, we will promptly notify affected users and relevant authorities as required by law.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                Your data may be stored or processed in other countries where our service providers operate, including the United States. We use Standard Contractual Clauses (SCCs) and similar safeguards to protect international data transfers.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>6. Your Rights and Choices</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#000000' }}>You have the right to:</h3>
              <ul className="list-disc list-inside space-y-1" style={{ color: '#000000' }}>
                <li>Access and update your personal information</li>
                <li>Request a copy of your data</li>
                <li>Delete your account and associated data</li>
                <li>Remove connected social accounts</li>
                <li>Control communication preferences</li>
              </ul>
              <p className="mt-4" style={{ color: '#000000' }}>
                To exercise these rights, please contact us at info@quixocial.com. We will respond within 30 days in accordance with applicable laws.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>7. Cookies and Tracking</h2>
            <div className="bg-indigo-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Maintain your login session</li>
                <li>Remember your preferences</li>
                <li>Analyze usage patterns</li>
                <li>Improve service performance</li>
              </ul>
              <p className="mt-4" style={{ color: '#000000' }}>
                You can control cookie settings through your browser preferences. Note that disabling cookies may affect service functionality.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>8. Changes to This Policy</h2>
            <div className="bg-yellow-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                We may update this Privacy Policy periodically. Updates will be posted on this page with a revised "Last Updated" date. Material changes will be communicated by email or in-app notification.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>9. Data Controller</h2>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6 border-l-4 border-blue-500">
              <p className="mb-4" style={{ color: '#000000' }}>
                For the purposes of data protection law, the data controller responsible for your personal information is:
              </p>
              <div className="bg-white p-5 rounded-lg mb-4 border border-blue-200">
                <p className="font-bold text-xl mb-2" style={{ color: '#1e40af' }}>Quixocial</p>
                <p className="text-sm" style={{ color: '#000000' }}>
                  As the data controller, I am responsible for determining how and why your personal data is processed, ensuring compliance with GDPR, CCPA, and other applicable data protection regulations.
                </p>
              </div>
              <p className="text-sm" style={{ color: '#666666' }}>
                This includes all Platform Data shared with us by Meta (Facebook, Instagram), X (Twitter), YouTube, and other connected social media platforms. I am committed to protecting your privacy rights and handling your data responsibly.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>10. Contact Information</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="mb-2" style={{ color: '#000000' }}>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-1" style={{ color: '#000000' }}>
                <p><strong>Email:</strong> info@quixocial.com</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-center" style={{ color: '#000000' }}>
                This Privacy Policy is designed to be comprehensive and compliant with major privacy regulations including GDPR, CCPA, and other applicable laws.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;